import React, { useState, useEffect } from 'react';
import { X, Crown, Coins, Zap, Shield, Plus, Minus, Calendar, Trash2, Clock, DollarSign } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  getUserDetail, grantCredits, grantPremium, revokePremium,
  grantEntitlement, revokeEntitlement
} from '../services/adminApi';
import { useDefaultAvatar } from '../services/defaultAvatar';

const ENTITLEMENT_OPTIONS = [
  { value: 'unlimited_messaging', label: 'Unlimited Messaging' },
  { value: 'unlimited_likes', label: 'Unlimited Likes' },
  { value: 'unlimited_profile_views', label: 'Unlimited Profile Views' },
  { value: 'advanced_search', label: 'Advanced Search' },
  { value: 'profile_boosts', label: 'Profile Boosts' },
  { value: 'super_likes', label: 'Super Likes' },
  { value: 'read_receipts', label: 'Read Receipts' },
  { value: 'premium_visibility', label: 'Premium Profile Visibility' },
  { value: 'ad_free', label: 'Ad-Free Experience' },
  { value: 'unlimited_credits', label: 'Unlimited Credits' },
];

const PREMIUM_DURATIONS = [
  { value: 1, label: '1 day' },
  { value: 7, label: '7 days' },
  { value: 30, label: '30 days' },
  { value: 90, label: '90 days' },
  { value: 365, label: '1 year' },
  { value: 0, label: 'Until further notice' },
];

const CREDIT_PRESETS = [10, 25, 50, 100, 250];

const AdminUserDetailModal = ({ userId, onClose, onUpdated }) => {
  const defaultAvatar = useDefaultAvatar();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState(null); // active action panel
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  // Form states
  const [creditAmount, setCreditAmount] = useState('');
  const [creditReason, setCreditReason] = useState('');
  const [premiumDuration, setPremiumDuration] = useState('30');
  const [premiumUntil, setPremiumUntil] = useState('');
  const [premiumReason, setPremiumReason] = useState('');
  const [entitlement, setEntitlement] = useState('unlimited_messaging');
  const [entitlementDuration, setEntitlementDuration] = useState('30');

  useEffect(() => {
    load();
  }, [userId]);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getUserDetail(userId);
      setDetail(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load user');
    } finally {
      setLoading(false);
    }
  };

  const handleGrantCredits = async (delta) => {
    const amount = parseInt(creditAmount) || 0;
    if (amount === 0) return;
    setBusy(true);
    setError('');
    try {
      await grantCredits(userId, delta * amount, creditReason);
      setCreditAmount('');
      setCreditReason('');
      setAction(null);
      await load();
      onUpdated?.();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed');
    } finally {
      setBusy(false);
    }
  };

  const handleGrantCreditPreset = async (amount) => {
    setBusy(true);
    setError('');
    try {
      await grantCredits(userId, amount, creditReason || `Admin grant: ${amount} credits`);
      setAction(null);
      await load();
      onUpdated?.();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed');
    } finally {
      setBusy(false);
    }
  };

  const handleGrantPremium = async () => {
    setBusy(true);
    setError('');
    try {
      const payload = {};
      if (premiumUntil) {
        payload.until = new Date(premiumUntil).toISOString();
      } else {
        payload.duration_days = parseInt(premiumDuration);
      }
      payload.reason = premiumReason;
      await grantPremium(userId, payload);
      setPremiumUntil('');
      setPremiumReason('');
      setAction(null);
      await load();
      onUpdated?.();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed');
    } finally {
      setBusy(false);
    }
  };

  const handleRevokePremium = async () => {
    if (!window.confirm('Revoke premium for this user?')) return;
    setBusy(true);
    try {
      await revokePremium(userId);
      await load();
      onUpdated?.();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed');
    } finally {
      setBusy(false);
    }
  };

  const handleGrantEntitlement = async () => {
    setBusy(true);
    setError('');
    try {
      const payload = { entitlement };
      const days = parseInt(entitlementDuration);
      if (days > 0) payload.duration_days = days;
      await grantEntitlement(userId, payload);
      setAction(null);
      await load();
      onUpdated?.();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed');
    } finally {
      setBusy(false);
    }
  };

  const handleRevokeEntitlement = async (entitlementId) => {
    if (!window.confirm('Revoke this entitlement?')) return;
    setBusy(true);
    try {
      await revokeEntitlement(userId, entitlementId);
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed');
    } finally {
      setBusy(false);
    }
  };

  const formatDate = (d) => {
    if (!d) return '—';
    try {
      return new Date(d).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' });
    } catch { return d; }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="admin-user-detail-modal"
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between rounded-t-3xl">
          <h2 className="text-xl font-bold text-gray-900" style={{ fontFamily: 'Outfit' }}>User Details</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full" data-testid="close-modal">
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading ? (
          <div className="p-12 text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-4 border-[#009E60] border-t-transparent mx-auto"></div>
          </div>
        ) : detail && (
          <div className="p-6 space-y-4">
            {error && (
              <div className="p-3 bg-red-50 text-red-600 rounded-xl text-sm" data-testid="modal-error">{error}</div>
            )}

            {/* User summary */}
            <div className="flex items-center gap-4">
              <img
                src={detail.user.photos?.[0] || defaultAvatar || 'https://images.unsplash.com/photo-1650702970095-f5b8ebeebce5?w=100'}
                alt=""
                className="w-16 h-16 rounded-full object-cover"
              />
              <div>
                <div className="font-bold text-lg text-gray-900">{detail.user.name}</div>
                <div className="text-sm text-gray-500">{detail.user.email}</div>
                <div className="text-xs text-gray-400 mt-1">ID: {detail.user.user_id}</div>
              </div>
            </div>

            {/* Summary Grid */}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-[#009E60]/5 border border-[#009E60]/20 rounded-2xl p-4">
                <div className="text-xs text-gray-500 mb-1">Subscription</div>
                <div className="font-semibold text-gray-900 capitalize">{detail.subscription.status}</div>
                {detail.subscription.premium_until && (
                  <div className="text-xs text-gray-500 mt-1">Until: {formatDate(detail.subscription.premium_until)}</div>
                )}
                {detail.subscription.granted_by_admin && (
                  <div className="text-xs text-[#FFCE00] mt-1 font-medium">Free Premium</div>
                )}
              </div>
              <div className="bg-[#FFCE00]/10 border border-[#FFCE00]/30 rounded-2xl p-4">
                <div className="text-xs text-gray-500 mb-1">Credits</div>
                <div className="font-bold text-2xl text-gray-900">{detail.credits.balance}</div>
              </div>
              <div className="bg-gray-50 rounded-2xl p-4">
                <div className="text-xs text-gray-500 mb-1">Entitlements</div>
                <div className="font-semibold text-gray-900">{detail.entitlements.length} active</div>
              </div>
              <div className="bg-gray-50 rounded-2xl p-4">
                <div className="text-xs text-gray-500 mb-1">Total Spent</div>
                <div className="font-semibold text-gray-900">${detail.total_spent.toFixed(2)}</div>
              </div>
            </div>

            {/* Active Entitlements */}
            {detail.entitlements.length > 0 && (
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Active Entitlements</h3>
                <div className="space-y-2">
                  {detail.entitlements.map(e => (
                    <div key={e.entitlement_id} className="flex items-center justify-between bg-white border border-gray-200 rounded-xl p-3">
                      <div>
                        <div className="font-medium text-gray-900 text-sm">{e.entitlement.replace(/_/g, ' ')}</div>
                        <div className="text-xs text-gray-500">
                          {e.expires_at ? `Expires ${formatDate(e.expires_at)}` : 'Until manually removed'}
                        </div>
                      </div>
                      <button
                        onClick={() => handleRevokeEntitlement(e.entitlement_id)}
                        className="text-red-500 hover:bg-red-50 p-2 rounded-full"
                        disabled={busy}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="grid grid-cols-2 gap-2">
              <Button
                onClick={() => setAction(action === 'credits' ? null : 'credits')}
                className={`h-11 rounded-xl ${action === 'credits' ? 'bg-[#009E60]' : 'bg-[#009E60]/10 text-[#009E60] hover:bg-[#009E60]/20'}`}
                data-testid="btn-give-credits"
              >
                <Coins className="w-4 h-4 mr-2" />
                {action === 'credits' ? 'Cancel' : 'Give Credits'}
              </Button>
              <Button
                onClick={() => setAction(action === 'premium' ? null : 'premium')}
                className={`h-11 rounded-xl ${action === 'premium' ? 'bg-[#FFCE00] text-[#0a0a0a]' : 'bg-[#FFCE00]/10 text-gray-900 hover:bg-[#FFCE00]/20'}`}
                data-testid="btn-give-premium"
              >
                <Crown className="w-4 h-4 mr-2" />
                {action === 'premium' ? 'Cancel' : 'Give Premium'}
              </Button>
              <Button
                onClick={() => setAction(action === 'entitlement' ? null : 'entitlement')}
                className={`h-11 rounded-xl ${action === 'entitlement' ? 'bg-blue-600 text-white' : 'bg-blue-50 text-blue-600 hover:bg-blue-100'}`}
                data-testid="btn-grant-entitlement"
              >
                <Zap className="w-4 h-4 mr-2" />
                {action === 'entitlement' ? 'Cancel' : 'Grant Entitlement'}
              </Button>
              {detail.subscription.is_premium && (
                <Button
                  onClick={handleRevokePremium}
                  variant="outline"
                  className="h-11 rounded-xl text-red-600 border-red-200 hover:bg-red-50"
                  disabled={busy}
                  data-testid="btn-revoke-premium"
                >
                  <Shield className="w-4 h-4 mr-2" />
                  Revoke Premium
                </Button>
              )}
            </div>

            {/* Credit Action Panel */}
            {action === 'credits' && (
              <div className="bg-[#009E60]/5 border-2 border-[#009E60]/20 rounded-2xl p-4 space-y-3">
                <h3 className="font-semibold text-gray-900">Manage Credits</h3>
                <div className="flex flex-wrap gap-2">
                  {CREDIT_PRESETS.map(preset => (
                    <button
                      key={preset}
                      onClick={() => handleGrantCreditPreset(preset)}
                      disabled={busy}
                      className="px-3 py-1.5 bg-white border border-[#009E60]/30 rounded-full text-sm hover:bg-[#009E60] hover:text-white transition-colors"
                      data-testid={`preset-${preset}`}
                    >
                      +{preset}
                    </button>
                  ))}
                </div>
                <Input
                  type="number"
                  value={creditAmount}
                  onChange={(e) => setCreditAmount(e.target.value)}
                  placeholder="Custom amount"
                  className="rounded-xl"
                  data-testid="credit-amount"
                />
                <Textarea
                  value={creditReason}
                  onChange={(e) => setCreditReason(e.target.value)}
                  placeholder="Reason (e.g., Customer service compensation)"
                  rows={2}
                  className="rounded-xl resize-none"
                  data-testid="credit-reason"
                />
                <div className="flex gap-2">
                  <Button
                    onClick={() => handleGrantCredits(1)}
                    disabled={busy || !creditAmount}
                    className="flex-1 bg-[#009E60] hover:bg-[#007A4B] rounded-xl"
                    data-testid="btn-add-credits"
                  >
                    <Plus className="w-4 h-4 mr-1" /> Add
                  </Button>
                  <Button
                    onClick={() => handleGrantCredits(-1)}
                    disabled={busy || !creditAmount}
                    variant="outline"
                    className="flex-1 text-red-600 border-red-200 hover:bg-red-50 rounded-xl"
                    data-testid="btn-remove-credits"
                  >
                    <Minus className="w-4 h-4 mr-1" /> Remove
                  </Button>
                </div>
              </div>
            )}

            {/* Premium Action Panel */}
            {action === 'premium' && (
              <div className="bg-[#FFCE00]/10 border-2 border-[#FFCE00]/30 rounded-2xl p-4 space-y-3">
                <h3 className="font-semibold text-gray-900">Grant Free Premium</h3>
                <div>
                  <Label className="text-sm">Duration</Label>
                  <Select value={premiumDuration} onValueChange={setPremiumDuration}>
                    <SelectTrigger className="mt-1 rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PREMIUM_DURATIONS.map(d => (
                        <SelectItem key={d.value} value={String(d.value)}>{d.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm">Or specific date & time</Label>
                  <Input
                    type="datetime-local"
                    value={premiumUntil}
                    onChange={(e) => setPremiumUntil(e.target.value)}
                    className="mt-1 rounded-xl"
                    data-testid="premium-until"
                  />
                </div>
                <Textarea
                  value={premiumReason}
                  onChange={(e) => setPremiumReason(e.target.value)}
                  placeholder="Reason (optional)"
                  rows={2}
                  className="rounded-xl resize-none"
                />
                <Button
                  onClick={handleGrantPremium}
                  disabled={busy}
                  className="w-full bg-[#FFCE00] hover:bg-[#e6b800] text-[#0a0a0a] rounded-xl font-semibold"
                  data-testid="btn-confirm-premium"
                >
                  <Crown className="w-4 h-4 mr-2" /> Grant Premium
                </Button>
              </div>
            )}

            {/* Entitlement Panel */}
            {action === 'entitlement' && (
              <div className="bg-blue-50 border-2 border-blue-200 rounded-2xl p-4 space-y-3">
                <h3 className="font-semibold text-gray-900">Grant Entitlement</h3>
                <div>
                  <Label className="text-sm">Entitlement</Label>
                  <Select value={entitlement} onValueChange={setEntitlement}>
                    <SelectTrigger className="mt-1 rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ENTITLEMENT_OPTIONS.map(e => (
                        <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm">Duration (days, 0 for permanent)</Label>
                  <Input
                    type="number"
                    value={entitlementDuration}
                    onChange={(e) => setEntitlementDuration(e.target.value)}
                    className="mt-1 rounded-xl"
                  />
                </div>
                <Button
                  onClick={handleGrantEntitlement}
                  disabled={busy}
                  className="w-full bg-blue-600 hover:bg-blue-700 rounded-xl"
                  data-testid="btn-confirm-entitlement"
                >
                  <Zap className="w-4 h-4 mr-2" /> Grant
                </Button>
              </div>
            )}

            {/* Transaction History */}
            {detail.transactions.length > 0 && (
              <div>
                <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <DollarSign className="w-4 h-4" /> Recent Transactions
                </h3>
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {detail.transactions.slice(0, 10).map((t, i) => (
                    <div key={i} className="flex items-center justify-between text-xs px-3 py-2 bg-gray-50 rounded-lg">
                      <div>
                        <div className="font-medium text-gray-900">{t.package_id} · {t.provider}</div>
                        <div className="text-gray-500">{formatDate(t.created_at)}</div>
                      </div>
                      <div className="text-right">
                        <div className="font-bold">${t.amount?.toFixed(2)}</div>
                        <div className={`${t.payment_status === 'paid' ? 'text-green-600' : 'text-gray-500'}`}>
                          {t.payment_status}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminUserDetailModal;
